import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown, ChevronUp, X } from 'lucide-react';
import * as React from 'react';

import { cn } from '@lib/utils';
import { TooltipComponent } from '../core/tooltip.component';
import { Separator } from './separator';

const Select = SelectPrimitive.Root;

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

type SelectTriggerProps = React.ComponentProps<typeof SelectPrimitive.Trigger> & {
  clearable?: boolean;
  hasError?: boolean;
  hasValue?: boolean;
  onClear?: () => void;
  tooltip?: React.ReactNode;
};

const SelectTrigger = (props: SelectTriggerProps) => {
  const { children, className, clearable, hasError, hasValue, onClear, tooltip, ...otherProps } = props;
  const ariaInvalid = hasError || otherProps['aria-invalid'];

  return (
    <SelectPrimitive.Trigger
      aria-invalid={ariaInvalid}
      className={cn(
        'flex h-10 w-full items-center justify-between',
        'rounded-md border border-gray-200 bg-gray-100 px-3 py-2 text-base md:text-sm',
        'box-border focus-visible:border-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-300',
        'aria-invalid:border-red-500 aria-invalid:focus-visible:border-red-500 aria-invalid:focus-visible:ring-red-200',
        'disabled:cursor-not-allowed disabled:opacity-70 [&>span]:line-clamp-1',
        'data-placeholder:text-gray-400 data-placeholder:hover:text-gray-400',
        '[&:not([data-placeholder])]:text-gray-600 [&:not([data-placeholder])]:hover:text-gray-600',
        className,
      )}
      {...otherProps}
    >
      {children}
      <div className="flex items-center ml-auto">
        {tooltip && (
          <TooltipComponent
            className="text-xs"
            triggerClassName="text-gray-500 bg-white"
          >
            {tooltip}
          </TooltipComponent>
        )}

        {clearable && hasValue && onClear && (
          <>
            <div
              aria-label="Clear selection"
              className="text-gray-400 hover:text-red-500 mr-2 cursor-pointer relative z-10 pointer-events-auto"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onClear();
              }}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              onPointerDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              role="button"
              style={{ pointerEvents: 'auto' }}
            >
              <X className="size-4 pointer-events-none" />
            </div>
            <Separator
              className="h-6 mr-2"
              orientation="vertical"
            />
          </>
        )}
        <SelectPrimitive.Icon asChild>
          <ChevronDown className="size-3.5 text-muted-foreground" />
        </SelectPrimitive.Icon>
      </div>
    </SelectPrimitive.Trigger>
  );
};
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = (props: React.ComponentProps<typeof SelectPrimitive.ScrollUpButton>) => {
  const { className, ...otherProps } = props;

  return (
    <SelectPrimitive.ScrollUpButton
      className={cn('flex cursor-default items-center justify-center py-1', className)}
      {...otherProps}
    >
      <ChevronUp className="h-4 w-4" />
    </SelectPrimitive.ScrollUpButton>
  );
};
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = (props: React.ComponentProps<typeof SelectPrimitive.ScrollDownButton>) => {
  const { className, ...otherProps } = props;

  return (
    <SelectPrimitive.ScrollDownButton
      className={cn('flex cursor-default items-center justify-center py-1', className)}
      {...otherProps}
    >
      <ChevronDown className="size-3.5" />
    </SelectPrimitive.ScrollDownButton>
  );
};
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName;

const SelectLabel = (props: React.ComponentProps<typeof SelectPrimitive.Label>) => {
  const { className, ...otherProps } = props;

  return (
    <SelectPrimitive.Label
      className={cn('py-1.5 pl-8 pr-2 text-sm font-semibold', className)}
      {...otherProps}
    />
  );
};
SelectLabel.displayName = SelectPrimitive.Label.displayName;

type SelectItemProps = React.ComponentProps<typeof SelectPrimitive.Item> & {
  // Optional helper text shown beneath the option label in the dropdown list only. It renders outside
  // Radix `ItemText`, so it never leaks into the trigger's selected-value display.
  description?: React.ReactNode;
  disabledTooltip?: React.ReactNode;
  hidden?: boolean;
};

const SelectItem = (props: SelectItemProps) => {
  const { children, className, description, disabledTooltip, hidden, textValue, ...otherProps } = props;

  return (
    <SelectPrimitive.Item
      className={cn(
        'relative flex w-full min-w-0 cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-disabled:cursor-not-allowed data-disabled:opacity-50',
        hidden && 'hidden',
        className,
      )}
      textValue={textValue}
      {...otherProps}
    >
      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <Check className="h-4 w-4" />
        </SelectPrimitive.ItemIndicator>
      </span>

      <div className="min-w-0 w-full *:flex *:w-full *:min-w-0">
        <SelectPrimitive.ItemText>
          {otherProps.disabled && disabledTooltip ? (
            <TooltipComponent
              side="right"
              trigger={<span className="block w-full truncate">{children}</span>}
            >
              {disabledTooltip}
            </TooltipComponent>
          ) : (
            <span className="block w-full truncate">{children}</span>
          )}
        </SelectPrimitive.ItemText>
        {description && (
          <span className="mt-0.5 block whitespace-normal text-xs font-normal text-muted-foreground">
            {description}
          </span>
        )}
      </div>
    </SelectPrimitive.Item>
  );
};
SelectItem.displayName = SelectPrimitive.Item.displayName;

type SelectContentChildProps = {
  children?: React.ReactNode;
  disabled?: boolean;
  hidden?: boolean;
};

type SelectContentProps = React.ComponentProps<typeof SelectPrimitive.Content> & {
  emptyMessage?: string;
};

const hasVisibleSelectableItem = (children: React.ReactNode): boolean => {
  for (const child of React.Children.toArray(children)) {
    if (!React.isValidElement<SelectContentChildProps>(child)) {
      continue;
    }

    if (child.type === SelectItem) {
      const { disabled = false, hidden = false } = child.props;
      if (!disabled && !hidden) {
        return true;
      }
      continue;
    }

    if (hasVisibleSelectableItem(child.props.children)) {
      return true;
    }
  }

  return false;
};

const SelectContent = (props: SelectContentProps) => {
  const { children, className, emptyMessage = 'No options available', position = 'popper', ...otherProps } = props;
  const hasSelectableOptions = hasVisibleSelectableItem(children);

  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        className={cn(
          'relative z-50 max-h-96 min-w-32 rounded-md border bg-popover text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
          position === 'popper' &&
            'w-[var(--radix-select-trigger-width)] max-w-[var(--radix-select-trigger-width)] data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
          className,
        )}
        position={position}
        {...otherProps}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport
          className={cn(
            'p-1',
            position === 'popper' && 'h-(--radix-select-trigger-height) w-full min-w-(--radix-select-trigger-width)',
          )}
        >
          {hasSelectableOptions ? (
            children
          ) : (
            <div className="px-2 py-1.5 text-sm text-muted-foreground">{emptyMessage}</div>
          )}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
};
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectSeparator = (props: React.ComponentProps<typeof SelectPrimitive.Separator>) => {
  const { className, ...otherProps } = props;

  return (
    <SelectPrimitive.Separator
      className={cn('-mx-1 my-1 h-px bg-muted', className)}
      {...otherProps}
    />
  );
};
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
};
